import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Link as LinkIcon, Copy, Trophy, Loader2, CheckCircle2, Share2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TID = {
  pickClient: "ref-pick-client",
  copyLink: "ref-copy-link",
  validate: (id) => `ref-validate-${id}`,
};

export default function TenantReferrals() {
  const { slug } = useParams();
  const [tab, setTab] = useState("share");
  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Loop viral B2B2C
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Indicação Premiada</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cada cliente é um canal de captação. Quem indica ganha pontos automaticamente.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="share">Gerar link</TabsTrigger>
          <TabsTrigger value="list">Indicações</TabsTrigger>
          <TabsTrigger value="leaderboard">Ranking</TabsTrigger>
        </TabsList>
        <TabsContent value="share" className="mt-6"><ShareTab /></TabsContent>
        <TabsContent value="list" className="mt-6"><ListTab /></TabsContent>
        <TabsContent value="leaderboard" className="mt-6"><Leaderboard /></TabsContent>
      </Tabs>
    </div>
  );
}

function ShareTab() {
  const [clients, setClients] = useState([]);
  const [client_id, setClientId] = useState("");
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tenant/clients", { params: { limit: 500 } });
      setClients(data.items || []);
    })();
  }, []);

  const generate = async () => {
    if (!client_id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/tenant/referrals/code/${client_id}`);
      setCode(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const fullLink = code ? `${window.location.origin}/r/${code.tenant_slug}/${code.code}` : "";

  const copy = async () => {
    if (!fullLink) return;
    await navigator.clipboard.writeText(fullLink);
    toast.success("Link copiado!");
  };

  const shareWhatsApp = () => {
    if (!code) return;
    const msg = `Olá! ${code.client_name} te indicou. Cadastre-se aqui: ${fullLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Card className="p-6 rounded-tenant border-border max-w-2xl">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <Label>Selecione o cliente que vai indicar</Label>
          <Select value={client_id} onValueChange={setClientId}>
            <SelectTrigger data-testid={TID.pickClient}><SelectValue placeholder="Escolher cliente" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={generate} disabled={!client_id || busy} className="gap-2"
          style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} <LinkIcon className="w-4 h-4" /> Gerar / mostrar link
        </Button>

        {code && (
          <div className="pt-5 border-t border-border space-y-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Link de indicação de {code.client_name}</div>
            <div className="flex gap-2">
              <Input readOnly value={fullLink} className="font-mono text-sm" />
              <Button variant="outline" onClick={copy} data-testid={TID.copyLink} className="gap-1.5">
                <Copy className="w-4 h-4" /> Copiar
              </Button>
              <Button variant="outline" onClick={shareWhatsApp} className="gap-1.5">
                <Share2 className="w-4 h-4" /> WhatsApp
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Quando alguém se cadastrar por este link, uma indicação é registrada automaticamente.
              Você pode validar manualmente ou deixar que o sistema valide assim que o indicado tiver o primeiro atendimento concluído.
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function ListTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant/referrals"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const validate = async (id) => {
    try {
      await api.post(`/tenant/referrals/${id}/validate`);
      toast.success("Indicação validada — pontos creditados");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const STATUS_STYLE = {
    pending: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    validated: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    rewarded: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    expired: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
  };

  return (
    <Card className="rounded-tenant border-border overflow-hidden">
      {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        items.length === 0 ? <div className="p-12 text-center text-muted-foreground">Nenhuma indicação ainda. Gere um link na aba ao lado.</div> :
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Quando</th>
              <th className="text-left px-5 py-3 font-medium">Indicador</th>
              <th className="text-left px-5 py-3 font-medium">Indicado</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-right px-5 py-3 font-medium">Ação</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-5 py-2.5 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-5 py-2.5">{r.referrer_name}</td>
                <td className="px-5 py-2.5">{r.referred_name || "—"}</td>
                <td className="px-5 py-2.5"><Badge variant="outline" className={STATUS_STYLE[r.status]}>{r.status}</Badge></td>
                <td className="px-5 py-2.5 text-right">
                  {r.status === "pending" && (
                    <Button size="sm" variant="ghost" data-testid={TID.validate(r.id)} className="gap-1.5 text-emerald-700"
                      onClick={() => validate(r.id)}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Validar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
    </Card>
  );
}

function Leaderboard() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/tenant/referrals/leaderboard"); setItems(data.items || []); })(); }, []);

  return (
    <Card className="rounded-tenant border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Trophy className="w-5 h-5 text-tenant" />
        <span className="font-display font-semibold text-lg">Top embaixadores</span>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">Sem indicações ainda.</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((row, i) => (
            <li key={row.client_id} className="px-6 py-3 flex items-center gap-4">
              <div className="font-display text-2xl font-bold text-tenant w-8">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{row.name}</div>
                <div className="text-xs text-muted-foreground">{row.validated} validadas de {row.total}</div>
              </div>
              <Badge style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                {row.total} {row.total === 1 ? "indicação" : "indicações"}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
